// Calendar-first demo flow (DemoRequestDialog, 2026-10-23): pick a time in the
// 369.dev calendar, then the details, then confirm. Pure: unit tested in
// tests/tracking/demoCalendarFirst.test.ts.
//
// The hard rule this module exists for: ONE lead POST and ONE Lead conversion
// (gtag demo_lead_captured + Meta Lead) per real lead, with the same names and
// parameters as the old flow. "Change time", Back and the calendar remounting
// must never post or fire again. Veronica's Meta ads optimise on that event.

/** What booking.js must report before the dialog relies on the time hand-off. */
export const CALENDAR_FIRST_CAPABILITIES = ['handoff-time', 'slot', 'legal-links'] as const;

/**
 * booking.js is cached and unversioned, so a visitor can hold a build without
 * the hand-off. Anything short of every capability means the old flow.
 */
export function supportsCalendarFirst(api: unknown): boolean {
  const caps = (api as { capabilities?: unknown } | null | undefined)?.capabilities;
  if (!Array.isArray(caps)) return false;
  return CALENDAR_FIRST_CAPABILITIES.every((cap) => caps.includes(cap));
}

export type LeadOnceResult<T> =
  | { status: 'posted'; body: T }
  | { status: 'already' }
  | { status: 'busy' }
  | { status: 'failed'; error: unknown };

/**
 * Posts the lead at most once per dialog session. A failed post can be retried
 * (nothing was captured); a successful one is never repeated, and a second call
 * while the first is in flight is refused rather than queued.
 */
export function createLeadOnce<T>(send: () => Promise<T>) {
  let posted = false;
  let inFlight = false;
  return {
    get posted() {
      return posted;
    },
    async run(): Promise<LeadOnceResult<T>> {
      if (posted) return { status: 'already' };
      if (inFlight) return { status: 'busy' };
      inFlight = true;
      try {
        const body = await send();
        posted = true;
        return { status: 'posted', body };
      } catch (error) {
        return { status: 'failed', error };
      } finally {
        inFlight = false;
      }
    },
  };
}

export type DemoSlot = { startMs: number; endMs: number };

/**
 * The lead's `answers`: the optional questions the visitor answered, plus the
 * time they picked so Sales sees intent even if the booking never completes.
 * Keys follow the lead API's answer-key rule; empty answers are left out.
 */
export function demoLeadAnswers(
  answers: Record<string, string>,
  slot: DemoSlot | null,
  timeZone: string,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(answers)) if (value) out[key] = value;
  if (slot) {
    out.preferredTime = new Date(slot.startMs).toISOString();
    if (timeZone) out.preferredTimeZone = timeZone.slice(0, 200);
  }
  return out;
}

/** "Mon, Oct 26 · 10:00 AM CDT" in the visitor's own zone. */
export function formatDemoSlot(slot: DemoSlot, locale: string, timeZone?: string): string {
  const day = new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', timeZone }).format(slot.startMs);
  const time = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone }).format(slot.startMs);
  return `${day} · ${time}`;
}
